import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { revertShotToLive, setVideoKeyframe } from '@/store/actions.ts';
import { capabilities } from '@/capabilities.ts';
import type { TimelineShot } from '@/types/timeline.ts';
import { APPROVAL_COLORS } from '@/export/playerConstants.ts';
import { isDroppableImageFile } from '@/hooks/useShotImageDrop.ts';
import { shotHasVideo } from '@/store/projectDerived.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import { shotMovementToNative } from '@/previs/converters.ts';
import { ShotMovementOverlay } from './SceneBoard/ShotMovementOverlay.tsx';
import { ShotImageDropConfirm } from './ShotImageDropConfirm.tsx';
import { showToast } from '@/components/Toast.tsx';
import i18next from 'i18next';
import styles from './ShotBar.module.css';

const APPROVAL_MARKER_COLORS = APPROVAL_COLORS;

interface ShotBarProps {
  shot: TimelineShot;
  sceneColor: string;
  startTime: number;
  pixelsPerSecond: number;
  onReorderStart?: (e: React.PointerEvent, shotId: number) => void;
  laneContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export function ShotBar({ shot, sceneColor, startTime, pixelsPerSecond, onReorderStart, laneContainerRef }: ShotBarProps) {
  const { t } = useTranslation('timeline');
  const selectedShotId = useProjectStore(s => s.selectedShotId);
  const selectShot = useProjectStore(s => s.selectShot);
  const updateShotDuration = useProjectStore(s => s.updateShotDuration);
  const _pushSnapshot = useUndoStore(s => s.pushSnapshot);
  const seek = usePlaybackStore(s => s.seek);
  const allApprovals = useCommentStore(s => s.approvals);
  const comments = useCommentStore(s => s.comments);
  const readOnly = useProjectStore(s => s.readOnly);
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const locked = readOnly || isPlaying;
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  const getTransitionAtTime = useProjectStore(s => s.getTransitionAtTime);

  const showInputImage = useAiStore(s => s.showInputImage);
  const getInputImageUrl = useAiStore(s => s.getInputImageUrl);

  const [dropActive, setDropActive] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [hMenuPos, setHMenuPos] = useState<{ x: number; y: number } | null>(null);
  const hMenuRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const openAiWorkspace = useUiStore(s => s.openAiWorkspace);
  const setCommentPanelOpen = useUiStore(s => s.setCommentPanelOpen);
  const isSelected = selectedShotId === shot.id;
  const GAP_INSET = 10;
  const width = Math.max(shot.duration * pixelsPerSecond - GAP_INSET * 2, 20);
  const left = startTime * pixelsPerSecond + GAP_INSET;

  const dragRef = useRef<{ startX: number; startDuration: number; shotId: number; isAnimatic: boolean } | null>(null);
  const autoScrollRef = useRef<number | null>(null);
  const lastPointerXRef = useRef(0);
  /** Accumulated pixels added by right-edge auto-scroll (not raw scrollLeft). */
  const autoScrollAccumRef = useRef(0);

  const approval = useMemo(() => {
    const shotApprovals = allApprovals.filter(a => a.shotId === shot.id);
    if (shotApprovals.length === 0) return null;
    return shotApprovals.reduce((latest, a) => a.revision > latest.revision ? a : latest);
  }, [allApprovals, shot.id]);

  const commentCount = useMemo(() => {
    return comments.filter(c => c.shotId === shot.id).length;
  }, [comments, shot.id]);

  const commentMarkerColor = APPROVAL_MARKER_COLORS[approval?.status ?? 'pending'] ?? '#f5c542';

  // The project's aspect ratio as a number, for the movement-arrow overlay: it sizes its arrows to
  // the letterboxed frame, and the wrapper below sizes that frame. ONE value feeds both, and it
  // falls back to the overlay's own 16:9 assumption when the field is absent or unparseable — the
  // two can never disagree and letterbox the arrows a second time. Guarded rather than handed
  // straight to parseAspectRatio, which splits its argument: review-session payloads can omit it.
  const projectAspectRatio = useProjectStore(s => s.project?.aspectRatio);
  const movementAspect = useMemo(() => {
    const ar = projectAspectRatio ? parseAspectRatio(projectAspectRatio) : 0;
    return ar > 0 ? ar : 16 / 9;
  }, [projectAspectRatio]);

  const isAnyGenerating = useAiStore(s => s.isAnyGenerating);

  const onBarPointerDown = useCallback((e: React.PointerEvent) => {
    if (!onReorderStart) return;
    if ((e.target as HTMLElement).dataset.dragHandle) return;
    if (isAnyGenerating()) return;
    if (locked) return;
    // Don't start reorder from fill area — onThumbnailClick handles that for H-menu
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.fill}`) || target.classList.contains(styles.fill)) return;
    onReorderStart(e, shot.id);
  }, [onReorderStart, shot.id, isAnyGenerating, locked]);

  const onBarClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.dragHandle) return;
    if (isAnyGenerating()) return;
    // If click came from the fill area (thumbnail) and we're not readOnly,
    // onThumbnailClick already handled it — don't double-fire
    const target = e.target as HTMLElement;
    if (!locked && (target.closest(`.${styles.fill}`) || target.classList.contains(styles.fill))) return;
    // Retap deselects, matching the board tile's `selectShot(isSelected ? null : shot.id, true)`
    // (20260824). No seek on the way out: deselecting says nothing about where the playhead
    // should be, and moving it would make "put that down" scrub the movie. NOT while playing —
    // usePlayback re-selects at every shot boundary, so a toggle there would flicker back on its
    // own. userInitiated stays FALSE on the select branch, exactly as before: that flag is the
    // board side panel's gate and the Timeline has never armed it.
    if (isSelected && !isPlaying) {
      setHMenuPos(null);
      selectShot(null);
      return;
    }
    selectShot(shot.id);
    let seekTime = startTime + getViewOffset();
    const transInfo = getTransitionAtTime(seekTime);
    if (transInfo && transInfo.toShot.id === shot.id && transInfo.transition.type !== 'continuous') {
      seekTime += transInfo.transition.duration / 2;
    }
    seek(seekTime);
  }, [shot.id, startTime, selectShot, seek, getViewOffset, getTransitionAtTime, isAnyGenerating, locked, isSelected, isPlaying]);

  // Auto-scroll loop: runs while pointer is near/past the right edge during duration drag
  const EDGE_ZONE = 60; // px from edge to start scrolling
  const SCROLL_SPEED = 400; // px/s at maximum proximity

  /** Compute new duration from pointer displacement + auto-scroll accumulator.
   *  Only auto-scroll pixels count — browser scroll clamping (from shrinking
   *  totalDuration) is excluded to avoid the feedback loop that accelerates
   *  shrinking. */
  const computeDuration = useCallback((drag: NonNullable<typeof dragRef.current>) => {
    const pointerDx = lastPointerXRef.current - drag.startX;
    return drag.startDuration + (pointerDx + autoScrollAccumRef.current) / pixelsPerSecond;
  }, [pixelsPerSecond]);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current !== null) return;
    let lastTime = performance.now();
    const tick = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const container = laneContainerRef?.current;
      const drag = dragRef.current;
      if (!container || !drag) { autoScrollRef.current = null; return; }

      const rect = container.getBoundingClientRect();
      const pointerX = lastPointerXRef.current;

      let scrolled = false;

      // Right edge auto-scroll (expand shot past visible area)
      const distFromRight = rect.right - pointerX;
      if (distFromRight < EDGE_ZONE) {
        const intensity = 1 - Math.max(0, distFromRight) / EDGE_ZONE;
        const scrollDelta = SCROLL_SPEED * intensity * dt;
        container.scrollLeft += scrollDelta;
        autoScrollAccumRef.current += scrollDelta;
        scrolled = true;
      }

      if (scrolled) {
        updateShotDuration(drag.shotId, computeDuration(drag), { pushUndo: false });
        useUiStore.getState().setScrollLeft(container.scrollLeft);
      }

      autoScrollRef.current = requestAnimationFrame(tick);
    };
    autoScrollRef.current = requestAnimationFrame(tick);
  }, [laneContainerRef, computeDuration, updateShotDuration]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current !== null) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }, []);

  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    _pushSnapshot();
    dragRef.current = {
      startX: e.clientX,
      startDuration: shot.duration,
      shotId: shot.id,
      isAnimatic: !!shot.isAnimatic,
    };
    autoScrollAccumRef.current = 0;
    lastPointerXRef.current = e.clientX;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    startAutoScroll();
  }, [shot.duration, shot.id, shot.isAnimatic, _pushSnapshot, startAutoScroll]);

  const onDragMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    lastPointerXRef.current = e.clientX;
    updateShotDuration(drag.shotId, computeDuration(drag), { pushUndo: false });
  }, [computeDuration, updateShotDuration]);

  const onDragEnd = useCallback(() => {
    stopAutoScroll();
    const drag = dragRef.current;
    if (drag?.isAnimatic && capabilities.bridge) {
      // Read the final (clamped) duration from the store
      const finalShot = useProjectStore.getState().allShots().find(s => s.id === drag.shotId);
      if (finalShot && Math.abs(finalShot.duration - drag.startDuration) > 0.05) {
        // Show confirmation dialog — user can cancel (revert) or confirm (reload)
        import('@/store/uiStore.ts').then(m =>
          m.useUiStore.getState().setAnimaticRegenPending({
            shotId: drag.shotId,
            newDuration: finalShot.duration,
            previousDuration: drag.startDuration,
          })
        );
      }
    }
    dragRef.current = null;
  }, [stopAutoScroll]);

  // File drag-drop handlers (image only — video file drops disabled by owner ruling 2026-08-25:
  // the old path attached a web-only video with no historyId, so Unity never learned about it and
  // the shot could carry an image AND a video at once. Proper video-file support is a future,
  // holistic feature; until then a dropped video file is simply ignored.)
  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDropActive(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setDropActive(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (isDroppableImageFile(file)) {
      // Confirm before replacing the shot with a static image (QA ask). The looser
      // MIME-or-extension filter matches the Import tab; the old exact-type list
      // (IMAGE_ACCEPT) silently dropped e.g. files with a missing MIME type.
      setPendingImageFile(file);
    }
  }, [shot.id]);

  // Ref guard: two Confirm taps in one React batch both see a non-null pendingImageFile
  // from the same closure — without it, double apply + double undo snapshot.
  const confirmImageBusyRef = useRef(false);
  const confirmDroppedImage = useCallback(() => {
    if (confirmImageBusyRef.current) return;
    const file = pendingImageFile;
    setPendingImageFile(null);
    if (!file) return;
    confirmImageBusyRef.current = true;
    import('@/store/actions.ts')
      .then(({ attachDroppedImage }) => attachDroppedImage(shot.id, file))
      .then((applied) => {
        if (applied) showToast(i18next.t('common:shotImageApplied'), 'success');
      })
      .catch(() => showToast(i18next.t('common:shotImageApplyFailed'), 'error'))
      .finally(() => { confirmImageBusyRef.current = false; });
  }, [pendingImageFile, shot.id]);

  // Close H-menu on outside click
  useEffect(() => {
    if (!hMenuPos) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (hMenuRef.current?.contains(target)) return;
      // The bar owns its own taps (same exemption as SceneSelector / AccountButton / TopHeader).
      // Without it, mousedown nulls hMenuPos BEFORE the browser dispatches click, so
      // onThumbnailClick's retap-deselect branch (guarded on hMenuPos !== null) reads a CLOSED
      // menu and re-selects + re-opens instead of deselecting — the toolbar blinks and the
      // selection never clears.
      if (barRef.current?.contains(target)) return;
      setHMenuPos(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [hMenuPos]);

  // Close H-menu when shot deselects
  useEffect(() => {
    if (!isSelected) setHMenuPos(null);
  }, [isSelected]);

  // Clamp H-menu to viewport so it doesn't get cut off by screen edges
  useEffect(() => {
    if (!hMenuPos) return;
    const el = hMenuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let x = hMenuPos.x;
    // The menu is centered via translateX(-50%), so rect.left/right reflect actual position
    if (rect.left < pad) {
      x += pad - rect.left;
    } else if (rect.right > window.innerWidth - pad) {
      x -= rect.right - (window.innerWidth - pad);
    }
    if (x !== hMenuPos.x) {
      setHMenuPos({ x, y: hMenuPos.y });
    }
  }, [hMenuPos]);

  const onThumbnailClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.dragHandle) return;
    if (isAnyGenerating()) return;
    if (locked) return;
    // Only trigger H-menu if click was on an actual thumbnail image
    const target = e.target as HTMLElement;
    const clickedImage = target.tagName === 'IMG' && target.classList.contains(styles.thumbnailImg);
    e.stopPropagation();
    // Retap deselects here too (20260824) — but never at the cost of the H-menu. A tap that is
    // about to OPEN the menu selects as before, so the shipped "click the bar to select, then the
    // thumbnail for Enhance / Comment / Speech" flow is untouched; only a tap that opens nothing
    // (off the image) or CLOSES an already-open menu toggles the selection off. Deselecting always
    // takes the menu with it: an H-menu floating over an unselected shot is a state nothing else
    // in the timeline can produce.
    if (isSelected && !isPlaying && !(clickedImage && hMenuPos === null)) {
      setHMenuPos(null);
      selectShot(null);
      return;
    }
    selectShot(shot.id);
    let seekTime = startTime + getViewOffset();
    const transInfo = getTransitionAtTime(seekTime);
    if (transInfo && transInfo.toShot.id === shot.id && transInfo.transition.type !== 'continuous') {
      seekTime += transInfo.transition.duration / 2;
    }
    seek(seekTime);
    // Position H-menu centered above the thumbnail image only
    if (clickedImage) {
      const bar = barRef.current;
      if (bar) {
        const img = bar.querySelector(`.${styles.thumbnailImg}`) as HTMLElement | null;
        const imgRect = img?.getBoundingClientRect();
        if (imgRect) {
          setHMenuPos(prev =>
            prev ? null : { x: imgRect.left + imgRect.width / 2 + 40, y: imgRect.top - 26 }
          );
        }
      }
    }
  }, [shot.id, startTime, selectShot, seek, getViewOffset, getTransitionAtTime, isAnyGenerating, locked, isSelected, isPlaying, hMenuPos]);

  const handleHMenuEnhance = useCallback(() => {
    setHMenuPos(null);
    selectShot(shot.id);
    openAiWorkspace('enhance', undefined, shot.id);
    // The workspace's landing tab became 'create' on 2026-08-25; this menu item means Enhance
    // THIS shot, so it names the tab instead of inheriting the default.
    useUiStore.getState().setAiWorkspaceTab('stylegrade');
  }, [shot.id, selectShot, openAiWorkspace]);

  const handleHMenuComment = useCallback(() => {
    setHMenuPos(null);
    selectShot(shot.id);
    setCommentPanelOpen(true);
  }, [shot.id, selectShot, setCommentPanelOpen]);

  const handleHMenuRevert = useCallback(() => {
    setHMenuPos(null);
    revertShotToLive(shot.id);
  }, [shot.id]);

  const handleHMenuSpeech = useCallback(() => {
    setHMenuPos(null);
    selectShot(shot.id);
    // Open audio workspace with Custom scope for this shot
    useUiStore.getState().setScratchScope('custom');
    useUiStore.getState().openAudioWorkspace('tts');
  }, [shot.id, selectShot]);

  const handleHMenuKeyframe = useCallback(() => {
    setHMenuPos(null);
    setVideoKeyframe(shot.id);
  }, [shot.id]);

  const durationText = shot.duration.toFixed(1) + 's';

  return (
    <div
      ref={barRef}
      className={`${styles.shotBar} ${isSelected ? styles.selected : ''} ${dropActive ? styles.dropActive : ''}`}
      style={{ left, width, ...(isAnyGenerating() && !isSelected ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
      onPointerDown={onBarPointerDown}
      onClick={onBarClick}
      onDragStart={e => e.preventDefault()}
      data-file-drop-target={locked ? undefined : ''}
      onDragOver={locked ? undefined : onDragOver}
      onDragLeave={locked ? undefined : onDragLeave}
      onDrop={locked ? undefined : onDrop}
    >
      <div
        className={styles.fill}
        style={shot.thumbnailDataUrl || shot.videoObjectUrl ? undefined : { background: sceneColor }}
        onClick={!locked ? onThumbnailClick : undefined}
      >
        {(() => {
          const inputUrl = showInputImage ? getInputImageUrl(shot.historyId) : null;
          const imgSrc = inputUrl ?? shot.thumbnailDataUrl;
          if (shot.videoObjectUrl) {
            return (
              <>
                {imgSrc ? (
                  <img className={styles.thumbnailImg} src={imgSrc} alt="" />
                ) : (
                  <span className={styles.placeholderNumber}>{shot.number}</span>
                )}
                <span className={styles.videoBadge} title={t('videoBadgeTitle')}>&#9654;</span>
              </>
            );
          }
          return imgSrc ? (
            <img className={styles.thumbnailImg} src={imgSrc} alt="" />
          ) : (
            <span className={styles.placeholderNumber}>{shot.number}</span>
          );
        })()}
        {/* Camera-movement arrows, the same composite Unity draws over its own shot lists and the
            three board surfaces mount. NOT a plain inset:0 layer over .fill: .thumbnailImg is
            height:100% with object-position:left, so a bar wider than its image leaves bare
            background to the right and a centred layer would drift off the frame. The wrapper
            takes the image's own box instead, and .shotBar's overflow:hidden clips the arrows
            exactly where it clips the image. Ahead of the badges in DOM order so they stay on
            top at the same z-index, matching the board tile. */}
        <span className={styles.movementFrame} style={{ aspectRatio: String(movementAspect) }}>
          <ShotMovementOverlay
            nativeMovement={shot.shotMovement ? shotMovementToNative(shot.shotMovement) : undefined}
            aspectRatio={movementAspect}
            containerAspect={movementAspect}
          />
        </span>
        {/* AI enhance badge — upper-left inside fill (mirrors videoBadge) */}
        {shot.historyId != null && (
          <span className={styles.enhanceBadge} title={t('aiEnhancedBadgeTitle')}>{'\u2726'}</span>
        )}
        {/* Comment dot — upper-right inside fill (visible only when comments exist) */}
        {commentCount > 0 && (
          <span
            className={styles.commentDot}
            style={{ background: commentMarkerColor }}
            title={commentCount > 1 ? t('commentCountTitlePlural', { count: commentCount }) : t('commentCountTitle', { count: commentCount })}
          />
        )}
        {/* Info pill — bottom-right overlay */}
        <div className={styles.info}>
          <span className={styles.shotNum}>{shot.number}</span>
          {durationText}
        </div>
      </div>
      {/* Drag handle for retiming — disabled in review mode and during playback */}
      {!locked && (
        <div
          className={styles.dragHandle}
          data-drag-handle="true"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        />
      )}
      {/* H-menu — horizontal pill above shot (portalled to body to escape overflow:hidden) */}
      {hMenuPos && isSelected && createPortal(
        <div
          ref={hMenuRef}
          className={styles.hMenu}
          style={{ left: hMenuPos.x, top: hMenuPos.y }}
          onClick={e => e.stopPropagation()}
        >
          {(shot.historyId != null || shot.hasStaticImage || shot.videoObjectUrl != null) && (
            <button className={styles.hMenuBtn} onClick={handleHMenuRevert}>{t('hMenuRevert')}</button>
          )}
          {shot.videoObjectUrl != null && (
            <button className={styles.hMenuBtn} onClick={handleHMenuKeyframe}>{t('hMenuPoster')}</button>
          )}
          <button className={styles.hMenuBtn} onClick={handleHMenuComment}>{t('hMenuComment')}</button>
          <button className={styles.hMenuBtn} onClick={handleHMenuEnhance}>{t('hMenuEnhance')}</button>
          <button className={styles.hMenuBtn} onClick={handleHMenuSpeech}>{t('hMenuSpeech')}</button>
        </div>,
        document.body
      )}
      {/* Image-drop confirm (always portalled by the component — the lane is overflow:hidden). */}
      {pendingImageFile && (
        <ShotImageDropConfirm
          shotLabel={shot.number}
          fileName={pendingImageFile.name}
          hasExistingMedia={shot.hasStaticImage === true || shotHasVideo(shot) || shot.isAnimatic === true}
          onConfirm={confirmDroppedImage}
          onCancel={() => setPendingImageFile(null)}
        />
      )}
    </div>
  );
}
